import { DriverWithStatusVehicleTripCountOutput } from '@allocab/allocab-driver-service-types';
import { MagnifyingGlassSolid } from '@allocab/allocab-icons';
import { useState } from 'react';

import { TextInput } from '../../../components/atomic/TextInput';
import { GradientButton } from '../../../components/button/GradientButton';
import { SecondaryButton } from '../../../components/button/SecondaryButton';
import { Modal } from '../../../components/modal/Modal';
import { Spinner } from '../../../components/Spinner';
import { useAssignDriverToTrip } from '../../../hooks/useAssignDriverToTrip';
import useDebounce from '../../../hooks/useDebounce';
import { useFethDriversPage } from '../../../hooks/useFetchDrivers';
import { ToastManager } from '../../../services/toast/utils';
import { t } from '../../../services/translation/translation';
import { getFullName, noop } from '../../../utils/common';

type Props = {
  onClose: () => void;
  tripKey: string;
};

export const TripAssignModal = ({ onClose, tripKey }: Props) => {
  const [selectedDriver, setSelectedDriver] = useState<DriverWithStatusVehicleTripCountOutput>();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { mutate: assignDriverToTrip, isLoading } = useAssignDriverToTrip(tripKey);
  const { data: drivers, isLoading: isLoadingDrivers } = useFethDriversPage(
    { direction: 'asc', orderBy: 'firstName', limit: 100, status: 'ACTIVATED', search: debouncedSearchQuery },
    1,
  );

  const handleClose = isLoading ? noop : onClose;

  const handleAssign = () => {
    if (selectedDriver) {
      assignDriverToTrip(
        { driverKey: selectedDriver.id },
        {
          onSuccess: () => {
            onClose();
            ToastManager.show({
              severity: 'success',
              message: t('trips-list-route.assign-modal.success', {
                fullName: getFullName(selectedDriver.firstName, selectedDriver.lastName),
              }),
            });
          },
        },
      );
    }
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header onClose={handleClose} title={t('trips-list-route.assign-modal.title')} />
      <Modal.Body>
        <p className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-3">
          {t('trips-list-route.assign-modal.description')}
        </p>
        <TextInput
          placeholder={t('invoicing-route.search-driver')}
          className="min-w-full xl:w-56 xl:min-w-0"
          value={searchQuery}
          onChange={e => setSearchQuery(e.currentTarget.value)}
          icon={<MagnifyingGlassSolid width={16} height={16} className="fill-current" />}
        />
        <div className="w-full bg-white divide-y divide-gray-100 rounded-lg shadow dark:bg-gray-800 dark:divide-gray-600 max-h-40 overflow-y-auto mt-4">
          <ul
            className="p-3 space-y-1 text-sm text-gray-700 dark:text-gray-200"
            aria-labelledby="dropdownRadioBgHoverButton"
          >
            {isLoadingDrivers ? <Spinner /> : null}
            {drivers && drivers?.items.length > 0
              ? drivers?.items.map(driver => (
                  <li key={driver.id}>
                    <div className="flex items-center pl-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer">
                      <input
                        id={`assign-radio-${driver.id}`}
                        type="radio"
                        checked={driver.id === selectedDriver?.id}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-700 dark:focus:ring-offset-gray-700 focus:ring-2 dark:bg-gray-600 dark:border-gray-500 cursor-pointer"
                        onChange={() => setSelectedDriver(driver)}
                      />
                      <label
                        htmlFor={`assign-radio-${driver.id}`}
                        className="w-full ml-2 p-2 text-sm font-medium text-gray-900 rounded dark:text-gray-300 cursor-pointer"
                      >
                        {getFullName(driver.firstName, driver.lastName)}
                      </label>
                    </div>
                  </li>
                ))
              : null}
            {drivers && drivers?.items.length === 0 && searchQuery.length > 0 ? (
              <p className="text-gray-500 dark:text-gray-400 italic">
                {t('trips-list-route.assign-modal.no-driver-found')}
              </p>
            ) : drivers && drivers?.items.length === 0 && searchQuery.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 italic">{t('trips-list-route.assign-modal.no-driver')}</p>
            ) : null}
          </ul>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <GradientButton onClick={handleAssign} isLoading={isLoading} isDisabled={!selectedDriver}>
          {t('common.assign')}
        </GradientButton>
        <SecondaryButton onClick={handleClose}>{t('common.cancel')}</SecondaryButton>
      </Modal.Footer>
    </Modal>
  );
};
